import { useCallback, useContext, useEffect } from 'react';
import { useEditContext } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { useLogoImage } from '@app/hooks/useLogoImage';
import { transformApiDrivenInput } from '@app/utils/transformUtils';
import { FIELD_TYPES, validateFormFields } from '@app/utils/validateUtils';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';
import EditView from '@app/components/common/views/EditView';

import { MediaForm } from './MediaForm';

const formFields = [
  { key: 'vod_url', type: FIELD_TYPES.TEXT, message: 'HLS URL is required' },
  { key: 'thumbnail_url', type: FIELD_TYPES.TEXT, message: 'Image URL is required' },
  { key: 'title', type: FIELD_TYPES.MULTILINGUAL, message: 'Title is required' },
  { key: 'duration', type: FIELD_TYPES.TEXT, message: 'Duration is required' },
  { key: 'expiry_date', type: FIELD_TYPES.FUTURE_DATE, message: 'Expiry date must be after today' },
  { key: 'taxonomy_id', type: FIELD_TYPES.SELECT, message: 'Primary topic is required' },
];

const nonRequiredFields = [
  { key: 'transcript', emptyValue: '', isMultilingual: true },
  { key: 'summary', emptyValue: '', isMultilingual: true },
  { key: 'subtitle', emptyValue: '', isMultilingual: true },
  { key: 'event_series_id', emptyValue: 0 },
  { key: 'taxonomy_id', emptyValue: 0 },
  { key: 'secondary_topic_ids', emptyValue: [] },
  { key: 'company_ids', emptyValue: [] }, // Bit misleading, actually needs companies
  { key: 'expert_ids', emptyValue: [] },
  { key: 'keywords', emptyValue: '', isMultilingual: true },
  { key: 'hosts', emptyValue: [] },
];

export const EditMedia = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const { saving } = useEditContext();
  const { language } = useFormLanguage();

  const { logoImage, handleChangeImageLogo, transform: logoTransform } = useLogoImage(nonRequiredFields);

  useEffect(() => {
    handleChangeLoading(!!saving);
  }, [saving, handleChangeLoading]);

  const validate = useCallback(
    (values: GenericObject) => validateFormFields({ fields: formFields, values, image: logoImage }),
    [logoImage]
  );

  /**
   * Workaround to turn the keywords into the format the API expects
   */
  const mediaTransform = (values: GenericObject) => {
    const v = logoTransform(values);
    // API expects company_ids to be updated
    // @ts-ignore
    v.company_ids = v.companies.map((company: any) => company.id);
    // API expects expert_ids to be updated too
    // @ts-ignore
    v.expert_ids = v.experts.map((expert: any) => expert.id);
    console.log('Values v:', v);
    return transformApiDrivenInput(v, 'keywords', language);
  };

  return (
    <div>
      <EditView transform={mediaTransform} resourceSingular="media" validate={validate} mutationMode="pessimistic">
        <MediaForm mediaLogo={logoImage} handleChangeMediaLogo={handleChangeImageLogo} title="EDIT MEDIA" />
      </EditView>
    </div>
  );
};
